import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Divider,
  ListItemText
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoOutlinedIcon
} from '@mui/icons-material';
import { fetchWithAuth, getSites, searchTenantUsersAdmin } from '../../api/backendApi';
import { useAuth } from '../../contexts/AuthContext';
import ButtonEditorDialog from '../../components/incidents/admin/ButtonEditorDialog';
import AssignmentEditorDialog from '../../components/incidents/admin/AssignmentEditorDialog';
import MainLayout from '../../components/layout/MainLayout';
import { userLabel } from '../../utils/userLabel';

const IncidentTypeEditorPage = () => {
  const navigate = useNavigate();
  const { typeKey } = useParams();
  const [searchParams] = useSearchParams();
  const { userData, tenantId: authTenantId, role: authRole } = useAuth();

  const isNew = typeKey === 'new';
  const isViewMode = searchParams.get('view') === 'true';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [siteOptions, setSiteOptions] = useState([]);
  const [userDisplayById, setUserDisplayById] = useState({});
  const [loadingSites, setLoadingSites] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    severity: 'med',
    siteIds: ['__ALL__'],
    active: true,
    allowMultipleActive: true,
    autoOpen: true,
    buttons: [],
    defaultAssignments: [],
    guardRules: {
      onePerSite: false,
      onePerTenant: false
    }
  });

  // Dialog state
  const [buttonDialogOpen, setButtonDialogOpen] = useState(false);
  const [editingButton, setEditingButton] = useState(null);
  const [editingButtonIndex, setEditingButtonIndex] = useState(null);

  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [editingAssignmentIndex, setEditingAssignmentIndex] = useState(null);

  const tenantId = authTenantId || userData?.tenantId;
  const role = authRole || userData?.role;

  const hasAdminAccess = ['admin', 'superadmin', 'tenant_admin', 'overseer'].includes(role);

  const normalizeButtons = (buttons) => {
    if (!Array.isArray(buttons)) return [];

    return buttons.map((b) => {
      if (!b || typeof b !== 'object') return b;

      if (b.actionType === 'OPEN_URL') {
        const url = String(b.url || b.endpoint || '').trim();
        const next = { ...b, url };
        delete next.endpoint;
        return next;
      }

      const next = { ...b };
      delete next.url;
      return next;
    });
  };

  // Load existing incident type
  useEffect(() => {
    if (isNew || !typeKey || !tenantId) {
      setLoading(false);
      return;
    }

    const loadIncidentType = async () => {
      try {
        const response = await fetchWithAuth(
          `/api/v2/tenants/${tenantId}/incident-types/${typeKey}`,
          'GET'
        );

        if (!response || !response.incidentType) {
          setError('Incident type not found');
          setLoading(false);
          return;
        }

        const data = response.incidentType;

        setFormData({
          name: data.name || '',
          key: data.key || typeKey,
          severity: data.severity || 'med',
          siteIds: Array.isArray(data.siteIds) && data.siteIds.length > 0 ? data.siteIds : ['__ALL__'],
          active: data.active !== undefined ? data.active : true,
          allowMultipleActive: data.allowMultipleActive !== undefined ? data.allowMultipleActive : true,
          autoOpen: data.autoOpen !== undefined ? data.autoOpen : true,
          buttons: normalizeButtons(data.buttons || []),
          defaultAssignments: data.defaultAssignments || [],
          guardRules: data.guardRules || {
            onePerSite: false,
            onePerTenant: false
          }
        });

        setLoading(false);
      } catch (err) {
        console.error('[INCIDENT-TYPE-EDITOR] Error loading incident type:', err);
        setError(err.message || 'Failed to load incident type');
        setLoading(false);
      }
    };

    loadIncidentType();
  }, [typeKey, isNew, tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    let active = true;
    const loadSites = async () => {
      try {
        setLoadingSites(true);
        const resp = await getSites();
        const items = Array.isArray(resp?.data)
          ? resp.data
          : (Array.isArray(resp) ? resp : (Array.isArray(resp?.sites) ? resp.sites : []));

        const normalized = (items || [])
          .map((s) => ({
            id: s?.id || s?.siteId || s?.key || '',
            name: (s?.name || s?.label || s?.id || s?.siteId || '').trim(),
            isDefault: !!s?.isDefault,
          }))
          .filter((s) => s.id);

        normalized.sort((a, b) => {
          if (a.isDefault && !b.isDefault) return -1;
          if (!a.isDefault && b.isDefault) return 1;
          return String(a.name || '').localeCompare(String(b.name || ''));
        });

        if (active) setSiteOptions(normalized);
      } catch (_e) {
        if (active) setSiteOptions([]);
      } finally {
        if (active) setLoadingSites(false);
      }
    };
    loadSites();
    return () => { active = false; };
  }, [tenantId]);

  useEffect(() => {
    const userTargetIds = Array.from(new Set(
      (formData.defaultAssignments || [])
        .filter((assignment) => assignment?.targetKind === 'user')
        .map((assignment) => String(assignment?.targetValue || '').trim())
        .filter(Boolean)
    ));

    if (!tenantId || userTargetIds.length === 0) return;

    let active = true;

    const resolveUsers = async () => {
      try {
        const users = await searchTenantUsersAdmin(tenantId, '', 100);

        const byId = new Map(
          (users || []).map((u) => [String(u?.id || u?.uid || '').trim(), u])
        );

        const resolvedEntries = userTargetIds.map((uid) => {
          const match = byId.get(uid);
          const name = userLabel(match || {});
          return [uid, name];
        });

        if (!active) return;
        setUserDisplayById((prev) => ({
          ...prev,
          ...Object.fromEntries(resolvedEntries),
        }));
      } catch (_e) {
        if (!active) return;
        setUserDisplayById((prev) => ({
          ...prev,
          ...Object.fromEntries(userTargetIds.map((uid) => [uid, prev[uid] || uid])),
        }));
      }
    };

    resolveUsers();

    return () => {
      active = false;
    };
  }, [tenantId, formData.defaultAssignments]);

  useEffect(() => {
    if (!isNew || isViewMode) return;

    const generatedKey = String(formData.name || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-');

    if (formData.key !== generatedKey) {
      setFormData(prev => ({
        ...prev,
        key: generatedKey,
      }));
    }
  }, [formData.name, formData.key, isNew, isViewMode]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGuardRuleChange = (rule, value) => {
    setFormData(prev => ({
      ...prev,
      guardRules: {
        ...prev.guardRules,
        [rule]: value
      }
    }));
  };

  // Button management
  const handleAddButton = () => {
    setEditingButton(null);
    setEditingButtonIndex(null);
    setButtonDialogOpen(true);
  };

  const handleEditButton = (button, index) => {
    setEditingButton(button);
    setEditingButtonIndex(index);
    setButtonDialogOpen(true);
  };

  const handleSaveButton = (button) => {
    if (editingButtonIndex !== null) {
      // Update existing button
      const newButtons = [...formData.buttons];
      newButtons[editingButtonIndex] = button;
      setFormData(prev => ({ ...prev, buttons: newButtons }));
    } else {
      // Add new button
      setFormData(prev => ({
        ...prev,
        buttons: [...prev.buttons, button]
      }));
    }
    setButtonDialogOpen(false);
  };

  const handleDeleteButton = (index) => {
    if (window.confirm('Are you sure you want to delete this button?')) {
      const newButtons = formData.buttons.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, buttons: newButtons }));
    }
  };

  // Assignment management
  const handleAddAssignment = () => {
    setEditingAssignment(null);
    setEditingAssignmentIndex(null);
    setAssignmentDialogOpen(true);
  };

  const handleEditAssignment = (assignment, index) => {
    setEditingAssignment(assignment);
    setEditingAssignmentIndex(index);
    setAssignmentDialogOpen(true);
  };

  const handleSaveAssignment = (assignment) => {
    if (editingAssignmentIndex !== null) {
      // Update existing assignment
      const newAssignments = [...formData.defaultAssignments];
      newAssignments[editingAssignmentIndex] = assignment;
      setFormData(prev => ({ ...prev, defaultAssignments: newAssignments }));
    } else {
      // Add new assignment
      setFormData(prev => ({
        ...prev,
        defaultAssignments: [...prev.defaultAssignments, assignment]
      }));
    }
    setAssignmentDialogOpen(false);
  };

  const handleDeleteAssignment = (index) => {
    if (window.confirm('Are you sure you want to delete this assignment?')) {
      const newAssignments = formData.defaultAssignments.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, defaultAssignments: newAssignments }));
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }

    if (!formData.key.trim()) {
      setError('Name must include letters or numbers');
      return false;
    }

    if (isNew && !/^[a-z0-9_-]+$/.test(formData.key)) {
      setError('Key must be lowercase alphanumeric with underscores or hyphens only');
      return false;
    }

    if (Array.isArray(formData.buttons)) {
      const missingUrl = formData.buttons.some((b) => {
        if (!b || typeof b !== 'object') return false;
        if (b.actionType !== 'OPEN_URL') return false;
        const url = String(b.url || b.endpoint || '').trim();
        return !url;
      });

      if (missingUrl) {
        setError('One or more OPEN_URL buttons are missing a URL');
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError(null);

    try {
      const typeData = {
        name: formData.name,
        key: formData.key,
        severity: formData.severity,
        siteIds: Array.isArray(formData.siteIds) ? formData.siteIds : ['__ALL__'],
        active: formData.active,
        allowMultipleActive: formData.allowMultipleActive,
        autoOpen: formData.autoOpen,
        buttons: normalizeButtons(formData.buttons),
        defaultAssignments: formData.defaultAssignments,
        guardRules: formData.guardRules
      };

      if (isNew) {
        const response = await fetchWithAuth(
          `/api/v2/tenants/${tenantId}/incident-types`,
          'POST',
          typeData
        );
        
        if (response.error) {
          setError(response.error);
          setSaving(false);
          return;
        }

        console.log('[INCIDENT-TYPE-EDITOR] Created incident type:', formData.key);
        setSuccessMessage('Incident type created successfully');
        
        // Navigate to edit page
        setTimeout(() => {
          navigate(`/admin/incidents/${formData.key}`);
        }, 1500);
      } else {
        const response = await fetchWithAuth(
          `/api/v2/tenants/${tenantId}/incident-types/${formData.key}`,
          'PUT',
          typeData
        );
        
        if (response.error) {
          setError(response.error);
          setSaving(false);
          return;
        }

        console.log('[INCIDENT-TYPE-EDITOR] Updated incident type:', formData.key);
        setSuccessMessage('Incident type updated successfully');
      }

      setSaving(false);
    } catch (err) {
      console.error('[INCIDENT-TYPE-EDITOR] Error saving incident type:', err);
      setError(err.message || 'Failed to save incident type');
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/incidents');
  };

  if (!hasAdminAccess) {
    return (
      <MainLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            You do not have permission to access this page. Admin access required.
          </Alert>
        </Box>
      </MainLayout>
    );
  }

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ p: 3 }}>
          <Typography>Loading incident type...</Typography>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/incidents')}
          sx={{ mb: 2 }}
        >
          Back to Incident Types
        </Button>
        <Typography variant="h3" sx={{ fontWeight: 600, mb: 1 }}>
          {isNew ? 'Create Incident Type' : isViewMode ? 'View Incident Type' : 'Edit Incident Type'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isNew ? 'Configure a new incident type' : 'Modify incident type configuration'}
        </Typography>
      </Box>

      {/* Error/Success Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* SECTION 1: Basic Info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Basic Information
            </Typography>
            <Tooltip title="Core incident type settings: name, sites, severity, and panel behavior.">
              <InfoOutlinedIcon fontSize="small" color="action" />
            </Tooltip>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr 1fr' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={isViewMode}
              required
              fullWidth
              helperText="Display name for this incident type"
            />

            <FormControl fullWidth sx={{ minWidth: 120 }}>
              <InputLabel>Severity</InputLabel>
              <Select
                value={formData.severity}
                onChange={(e) => handleInputChange('severity', e.target.value)}
                disabled={isViewMode}
                label="Severity"
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="med">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={isViewMode || loadingSites}>
              <InputLabel id="incident-type-sites">Sites</InputLabel>
              <Select
                labelId="incident-type-sites"
                label="Sites"
                multiple
                value={Array.isArray(formData.siteIds) ? formData.siteIds : ['__ALL__']}
                onChange={(e) => {
                  const next = Array.isArray(e.target.value) ? e.target.value : [];
                  const hasAll = next.includes('__ALL__');

                  if (hasAll && next.length > 1) {
                    // User selected a specific site while All Sites was selected.
                    // Prefer the explicit site selection by removing the '__ALL__' sentinel.
                    handleInputChange('siteIds', next.filter((x) => x !== '__ALL__'));
                    return;
                  }

                  // Keep '__ALL__' as exclusive when it's the only selection.
                  handleInputChange('siteIds', hasAll ? ['__ALL__'] : next);
                }}
                renderValue={(selected) => {
                  const sel = Array.isArray(selected) ? selected : [];
                  if (sel.includes('__ALL__')) return 'All Sites';
                  const byId = new Map((siteOptions || []).map(s => [s.id, s.name || s.id]));
                  return sel.map((id) => byId.get(id) || id).join(', ');
                }}
              >
                <MenuItem value="__ALL__">
                  <Checkbox checked={Array.isArray(formData.siteIds) && formData.siteIds.includes('__ALL__')} />
                  <ListItemText primary="All Sites" />
                </MenuItem>
                {(siteOptions || []).map((s) => (
                  <MenuItem
                    key={s.id}
                    value={s.id}
                  >
                    <Checkbox checked={Array.isArray(formData.siteIds) && formData.siteIds.includes(s.id)} />
                    <ListItemText primary={s.name || s.id} secondary={s.id} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box
              sx={{
                gridColumn: '1 / -1',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                gap: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={(e) => handleInputChange('active', e.target.checked)}
                    disabled={isViewMode}
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.allowMultipleActive}
                    onChange={(e) => handleInputChange('allowMultipleActive', e.target.checked)}
                    disabled={isViewMode}
                  />
                }
                label="Allow Multiple Active Incidents"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.autoOpen}
                    onChange={(e) => handleInputChange('autoOpen', e.target.checked)}
                    disabled={isViewMode}
                  />
                }
                label="Auto-Open Panel"
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* SECTION 2: Panel Buttons */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Panel Buttons
            </Typography>
            {!isViewMode && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddButton}
                size="small"
              >
                Add Button
              </Button>
            )}
          </Box>

          {formData.buttons.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No buttons configured. Add buttons to define actions available in the incident panel.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Order</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell>Action Type</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>Cooldown</TableCell>
                  <TableCell>Confirmation</TableCell>
                  {!isViewMode && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.buttons.map((button, index) => (
                  <TableRow key={index}>
                    <TableCell>{button.order || index + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontSize: '1.2rem' }}>{button.icon}</span>
                        {button.label}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={button.actionType} size="small" />
                    </TableCell>
                    <TableCell>
                      {button.visibleRoles?.join(', ') || 'All'}
                    </TableCell>
                    <TableCell>{button.cooldownSeconds || 0}s</TableCell>
                    <TableCell>
                      {button.requiresConfirmation ? (
                        <Chip label="Yes" color="warning" size="small" />
                      ) : (
                        <Chip label="No" variant="outlined" size="small" />
                      )}
                    </TableCell>
                    {!isViewMode && (
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleEditButton(button, index)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteButton(index)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* SECTION 3: Default Assignments */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Default Assignments
              </Typography>
              <Tooltip title="Automatic recipients (users or roles) added when a new incident of this type is created.">
                <InfoOutlinedIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>
            {!isViewMode && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddAssignment}
                size="small"
              >
                Add Assignment
              </Button>
            )}
          </Box>

          {formData.defaultAssignments.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No default assignments configured. Add assignments to automatically notify users/roles when incidents are created.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Target Type</TableCell>
                  <TableCell>Target Value</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Auto-Open</TableCell>
                  {!isViewMode && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.defaultAssignments.map((assignment, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Chip
                        label={assignment.targetKind === 'role' ? 'Role' : 'User'}
                        color={assignment.targetKind === 'role' ? 'primary' : 'secondary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{assignment.targetKind === 'user' ? (userDisplayById[assignment.targetValue] || assignment.targetValue) : assignment.targetValue}</TableCell>
                    <TableCell>{assignment.priority || 0}</TableCell>
                    <TableCell>
                      {assignment.autoOpen ? (
                        <Chip label="Yes" color="success" size="small" />
                      ) : (
                        <Chip label="No" variant="outlined" size="small" />
                      )}
                    </TableCell>
                    {!isViewMode && (
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleEditAssignment(assignment, index)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteAssignment(index)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* SECTION 4: Guard Rules */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Guard Rules
            </Typography>
            <Tooltip title="Safety limits that prevent duplicate active incidents for this type.">
              <InfoOutlinedIcon fontSize="small" color="action" />
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.guardRules.onePerSite}
                  onChange={(e) => handleGuardRuleChange('onePerSite', e.target.checked)}
                  disabled={isViewMode}
                />
              }
              label="Only One Active Per Site"
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
              Prevents creating multiple active incidents of this type for the same site
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.guardRules.onePerTenant}
                  onChange={(e) => handleGuardRuleChange('onePerTenant', e.target.checked)}
                  disabled={isViewMode}
                />
              }
              label="Only One Active Per Tenant"
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
              Prevents creating multiple active incidents of this type for the entire tenant
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Sticky Footer */}
      {!isViewMode && (
        <Paper
          sx={{
            position: 'sticky',
            bottom: 0,
            p: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            zIndex: 10
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Paper>
      )}

      {/* Dialogs */}
      <ButtonEditorDialog
        open={buttonDialogOpen}
        button={editingButton}
        onClose={() => setButtonDialogOpen(false)}
        onSave={handleSaveButton}
      />

      <AssignmentEditorDialog
        open={assignmentDialogOpen}
        assignment={editingAssignment}
        onClose={() => setAssignmentDialogOpen(false)}
        onSave={handleSaveAssignment}
      />
      </Box>
    </MainLayout>
  );
};

export default IncidentTypeEditorPage;
